import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TbBell, TbLogout, TbDownload, TbArrowBackUp, TbLayoutSidebarLeftExpand, TbLayoutSidebarLeftCollapse,
         TbMenu2, TbChevronRight, TbLock, TbSparkles, TbHelpCircle } from "react-icons/tb";
import { useTheme } from "../../ThemeContext";
import { useUiVersion } from "../../UiVersionContext";
import { api } from "../lib/api";
import { MENUS, DEFAULT_SCREEN, menuForScreen, crumbsFor, isOffNav, isAccountScreen,
         pathForScreen, screenForPath, screenForTab, lockForScreen } from "./nav";
import { PlanContext, usePlanLoader } from "../lib/plan";
import { LockedScreen, PlanPrompts, TrialBanner } from "./PlanGate";
import { resolveScreen } from "./registry";
import AccountMenu from "./AccountMenu";
import ThemeToggle from "./ThemeToggle";
import NavFlyout, { hasFlyout } from "./NavFlyout";
import { startPageTour, startWelcomeTour } from "./tour";
import { hasPageTour, tourKeyFor } from "./tours";
import { normalizeTemplateId } from "../../services/templateService";
import { Button } from "../components";
import "../styles/tokens.css";
import "./shell.css";

// The v2 shell: the menus in the rail, each opening a flyout (NavFlyout.jsx) on hover
// with its pages, its "New …" actions and its live data, and a 44px top bar carrying the
// breadcrumb and the global actions.
//
// It renders only v2 screens. Nothing under src/v2 imports a v1 screen, which is what
// keeps the two interfaces from bleeding into each other.

const ShellV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const { setUiVersion, syncFromServer } = useUiVersion();

  // The URL decides which screen is open — every screen has its own path (nav.js
  // SCREEN_PATHS), so it can be bookmarked, shared, refreshed and reached with Back.
  //
  // An old /dashboard?tab=x link still lands on the right screen: it is rendered
  // straight away and the address is rewritten to the screen's own path just below.
  const rawTab = new URLSearchParams(location.search).get("tab");
  const legacyScreen = rawTab != null ? screenForTab(rawTab) : null;
  const routed = screenForPath(location.pathname);
  const screen = legacyScreen || routed?.key || DEFAULT_SCREEN;
  // The id in /reviews/assessments/:id. Null everywhere else.
  const routeParam = legacyScreen ? null : routed?.param ?? null;

  useEffect(() => {
    // replace, not push: Back must not land on the ?tab= address and bounce forward again.
    if (rawTab != null) navigate(pathForScreen(screen), { replace: true });
  }, [rawTab, screen, navigate]);

  // The rail follows the screen unless the user picks a menu whose items they have not
  // opened yet, so it is state seeded from the screen rather than derived from it.
  const [menu, setMenu] = useState(menuForScreen(screen));

  // Labels show by default. With the context column gone the rail is the only place the
  // area names appear, and an icon-only rail is only legible once you know the icons.
  // Someone who collapsed it keeps it collapsed.
  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("v2RailOpen") !== "0"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("v2RailOpen", railOpen ? "1" : "0"); } catch { /* private mode */ }
  }, [railOpen]);

  // The rail flyout. Which menu's panel is open, and the rail item it sits against.
  //
  // Hover intent, so the panel behaves like a menu rather than a tooltip: it opens after a
  // short pause (sweeping the pointer down the rail to reach the avatar should not flash
  // four panels), switches instantly between items once one is open, and closes after a
  // grace period so the pointer can cross the gap from the rail item into the panel.
  const [flyKey, setFlyKey] = useState(null);
  const [flyAnchor, setFlyAnchor] = useState(null);
  const flyTimer = useRef(null);
  const flyRef = useRef(null);
  const railRef = useRef(null);
  // The last pointer type on a rail item. Touch has no hover, so a tap opens the panel
  // first and a second tap goes; a mouse click always goes.
  const pointerType = useRef("mouse");

  const clearFlyTimer = () => { clearTimeout(flyTimer.current); flyTimer.current = null; };
  const openFly = (key, el, delay) => {
    clearFlyTimer();
    const show = () => {
      // Both panels anchor to the same rail and would overlap. The account panel also
      // opens on hover now, so moving from the avatar up to a menu has to hand over.
      clearAccountTimer();
      setAccountOpen(false);
      setFlyAnchor(el.getBoundingClientRect());
      setFlyKey(key);
    };
    if (delay) flyTimer.current = setTimeout(show, delay);
    else show();
  };
  const closeFlySoon = () => {
    clearFlyTimer();
    flyTimer.current = setTimeout(() => setFlyKey(null), 220);
  };
  useEffect(() => () => clearFlyTimer(), []);

  // The account panel opens on hover, like every other rail item.
  //
  // It used to be the one thing in the rail that required a click, so Personal settings
  // and Tenant admin sat behind a different interaction from everything beside them —
  // which reads as "this one is not a menu" rather than as a deliberate difference.
  //
  // Same timings as the flyout: a short delay before opening so passing the pointer over
  // it on the way to Sign out does not flash the panel, and a grace period on the way out
  // so the diagonal from the avatar to the panel does not dismiss it mid-travel.
  // The rail, off-canvas on a phone.
  //
  // Below the drawer breakpoint the rail is a panel that slides in over the page instead
  // of a column beside it: 52px of a 320px viewport is a sixth of the screen spent on
  // navigation nobody is looking at, and the hover flyouts it depends on do not exist on
  // touch. Inside the drawer each area lists its own screens, so getting anywhere is one
  // tap rather than the two-tap "open the flyout, then choose" dance.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Any navigation closes it. Without this the drawer stays over the screen it just
  // opened, which reads as the tap having failed.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const accountRef = useRef(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountTimer = useRef(null);
  const clearAccountTimer = () => { clearTimeout(accountTimer.current); accountTimer.current = null; };
  const openAccountSoon = (delay = 90) => {
    clearAccountTimer();
    // Two panels anchored to the same rail, open at once, would overlap.
    closeFlySoon();
    accountTimer.current = setTimeout(() => setAccountOpen(true), delay);
  };
  const closeAccountSoon = () => {
    clearAccountTimer();
    accountTimer.current = setTimeout(() => setAccountOpen(false), 220);
  };
  useEffect(() => () => clearAccountTimer(), []);

  const [me, setMe] = useState(null);
  // The Business reviews organization filter. "" is every organization.
  //
  // It lives here rather than in a screen because screens unmount on navigation, so a
  // selection held in one would reset every time you moved between reviews screens. Not
  // persisted: it resets to All on reload, which also means there is no stored id to go
  // stale if an organization is later deleted.
  const [organizations, setOrganizations] = useState([]);
  const [reviewOrgId, setReviewOrgId] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The plan comes from usePlanLoader below (s1/api/entitlements), which carries
        // the licence as well as each feature switch.
        const [meData, orgs] = await Promise.all([
          api.get("s1/api/me").catch(() => null),
          api.get("s1/api/organizations").catch(() => null),
        ]);
        if (cancelled) return;
        if (meData) {
          setMe(meData);
          // The stored preference is the source of truth; the cache only decided what
          // to paint before this reply arrived.
          syncFromServer(meData.ui_version);
        }
        if (orgs) {
          // Fetched once here and shared: the Business reviews filter and the Accounts
          // flyout both list it, so neither makes a request of its own.
          const list = orgs.organizations || [];
          setOrganizations(list);
        }
      } catch (e) {
        // A failed load must not blank the shell: the nav still works and every screen
        // fetches its own data.
        console.error("[v2] shell load failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [syncFromServer]);

  const isAdmin = me?.isadmin === 1;
  const plan = usePlanLoader();
  const isPro = plan.isPro;

  // Admin-only items are hidden from everyone else. Plan-gated ones are NOT hidden: they
  // stay in the nav marked `locked`, and opening one shows the upgrade screen.
  const canSee = useCallback((x) => !x.admin || isAdmin, [isAdmin]);
  const { locked: planLocked } = plan;

  const menus = useMemo(
    () => MENUS.filter(canSee).map((m) => {
      const menuLocked = planLocked(m);
      return {
        ...m,
        locked: menuLocked,
        items: m.items.filter(canSee).map((i) => ({ ...i, locked: menuLocked || planLocked(i) })),
      };
    }).filter((m) => m.items.length),
    [canSee, planLocked]
  );
  const currentMenu = menus.find((m) => m.key === menu) || menus[0];

  // Opens a screen by changing the URL. `param` is the id for screens that take one.
  // `intent` is a one-shot request for the screen to act on — open its New dialog, open
  // one record — carried in history state (see intent.js).
  const go = (key, param, intent) => {
    const path = pathForScreen(key, param);
    const same = path === location.pathname && !location.search;
    if (intent) navigate(path, { replace: same, state: { navIntent: intent } });
    else if (!same) navigate(path);
    setFlyKey(null);

    // Off-nav screens — Administration and everything under it, notifications, the
    // assessment editor — have no rail area of their own. menuForScreen falls back to
    // the FIRST menu for anything it does not recognise, which would select Organizations
    // and render its context nav beside an unrelated screen, highlighting nothing. Leave
    // the rail where it was instead.
    if (isOffNav(key)) return;

    // A screen can belong to more than one menu. menuForScreen would always send us to
    // the first, which would yank the rail out from under someone who opened the screen
    // from the other one. Stay put when the current menu also carries the key.
    setMenu((current) =>
      menus.find((m) => m.key === current)?.items.some((i) => i.key === key)
        ? current
        : menuForScreen(key)
    );
  };

  // Back and Forward change the screen without going through go(), so the rail has to
  // catch up here: move to the screen's menu unless the current one already carries it,
  // and leave it alone for off-nav screens, exactly as go() does.
  useEffect(() => {
    if (isOffNav(screen)) return;
    setMenu((current) =>
      MENUS.find((m) => m.key === current)?.items.some((i) => i.key === screen)
        ? current
        : menuForScreen(screen)
    );
  }, [screen]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    navigate("/login");
  };

  // Flyout actions that are not a plain screen change.
  //
  // The builder is outside the shell and reads a specific state shape — the same two the
  // Template hub sends (TemplateHubScreen.jsx), so the builder cannot tell them apart.
  const openBuilder = (action) => {
    setFlyKey(null);
    if (action === "new-template") {
      navigate("/report-builder", { state: { mode: "template", scanData: null, fromTemplateHub: true } });
    }
  };
  const openTemplate = (t) => {
    setFlyKey(null);
    navigate("/report-builder", { state: { mode: "template", templateId: normalizeTemplateId(t), fromTemplateHub: true } });
  };
  // A review coming up is opened by narrowing Business reviews to that client — the same
  // filter as the top-bar select — and landing on its overview.
  const pickReviewOrg = (orgId) => {
    setReviewOrgId(String(orgId));
    go("review-overview");
  };

  // The product tour (tour.js). It runs on the dashboard, so asking for it from anywhere
  // else goes there first; `tourPending` carries the request across that navigation.
  //
  // It starts once for a user whose /me says they have not seen it, and again whenever
  // they ask from the account menu. However it ends — finished, skipped, closed — counts
  // as seen: someone who closes it on step one has made their choice, and starting it
  // again on every visit would be the fastest way to make them resent it.
  const [tourPending, setTourPending] = useState(false);
  const autoTourDone = useRef(false);
  const requestTour = useCallback(() => {
    setFlyKey(null);
    setDrawerOpen(false);
    setTourPending(true);
  }, []);

  // How it works: the tour of whichever screen is open, started from the top bar. It
  // stays on that screen; leaving the screen ends it (the effect below).
  const pageTour = useRef(null);
  const runPageTour = useCallback(() => {
    if (tourPending || pageTour.current) return;
    setFlyKey(null);
    setDrawerOpen(false);
    pageTour.current = startPageTour(tourKeyFor(screen), { onDone: () => { pageTour.current = null; } });
  }, [tourPending, screen]);
  useEffect(() => {
    window.addEventListener('v2:page-tour', runPageTour);
    return () => window.removeEventListener('v2:page-tour', runPageTour);
  }, [runPageTour]);
  useEffect(() => () => { pageTour.current?.destroy(); }, [screen]);

  useEffect(() => {
    if (autoTourDone.current || me?.tour_completed !== false) return;
    autoTourDone.current = true;
    requestTour();
  }, [me, requestTour]);

  useEffect(() => {
    if (!tourPending) return undefined;
    if (screen !== DEFAULT_SCREEN) { go(DEFAULT_SCREEN); return undefined; }

    // Wait for the dashboard to have something to point at, and for any dialog (the plan
    // welcome, an upgrade prompt) to be out of the way — a tour under a modal is a tour
    // nobody can see. The dashboard's data is given a few seconds; past that the tour
    // starts without the cards that have not loaded, rather than not at all.
    let tour = null;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const ready = document.querySelector('[data-tour="quick-actions"]');
      const loaded = document.querySelector('[data-tour="attention"]') || Date.now() - startedAt > 4000;
      const blocked = document.querySelector('[role="dialog"][aria-modal="true"]');
      if (!ready || !loaded || blocked) return;
      clearInterval(timer);
      const rawFirst = (me?.name || "").split(/[\s@.]/)[0];
      tour = startWelcomeTour({
        firstName: rawFirst ? rawFirst[0].toUpperCase() + rawFirst.slice(1) : "",
        onDone: () => {
          tour = null;
          setTourPending(false);
          if (me?.tour_completed === false) {
            setMe((m) => (m ? { ...m, tour_completed: true } : m));
            api.post("s1/api/me/tour").catch((e) => console.warn("[v2] could not save tour progress", e));
          }
        },
      });
      if (!tour) setTourPending(false);
    }, 250);
    return () => {
      clearInterval(timer);
      // Navigating away mid-tour (the back button still works) leaves it pointing at a
      // screen that is gone. Ending it here also runs onDone, which clears the request.
      if (tour) tour.destroy();
    };
    // `go` is recreated every render; the request only needs re-checking when the
    // screen changes or a new one is made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourPending, screen]);

  // Escape closes the flyout and puts focus back on its rail item; a press anywhere
  // outside the rail and the panel closes it.
  useEffect(() => {
    if (!flyKey) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const inPanel = flyRef.current?.contains(document.activeElement);
      setFlyKey(null);
      if (inPanel) railRef.current?.querySelector(`[data-menu="${flyKey}"]`)?.focus();
    };
    const onDown = (e) => {
      if (railRef.current?.contains(e.target) || flyRef.current?.contains(e.target)) return;
      setFlyKey(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [flyKey]);

  // Settings are reached from the account panel, not the rail, so while one is open no
  // rail area is the current one. Leaving Organizations lit behind the Profile screen
  // said the user was somewhere they were not — and it is the rail, not the breadcrumb,
  // that people scan to answer "where am I". The account trigger takes the highlight
  // instead, because that is the surface these screens actually hang off.
  //
  // Narrower than isOffNav on purpose: the assessment editor is off-nav too, but it is
  // opened from Business reviews and belongs to it, so that rail item stays lit.
  const inAccount = isAccountScreen(screen);

  // A new screen starts at the top. .v2-main is the scroller, not the window, so the
  // browser's own scroll restoration does nothing here.
  const mainRef = useRef(null);
  useEffect(() => { if (mainRef.current) mainRef.current.scrollTop = 0; }, [location.pathname]);

  const Screen = resolveScreen(screen);
  // A screen the plan does not include renders the lock screen instead — whether it was
  // reached from the nav, a dashboard shortcut or a typed URL.
  const screenLock = lockForScreen(screen);
  const screenLocked = !!screenLock && planLocked(screenLock);

  // An id that is no longer in the list — the organization was deleted in another tab —
  // must not silently filter everything down to nothing. Fall back to all.
  const effectiveOrgId =
    reviewOrgId && organizations.some((o) => String(o.id) === String(reviewOrgId))
      ? reviewOrgId
      : "";

  // Assessment templates carry no organization and are reused across clients, so the
  // filter is meaningless there and the control is hidden rather than shown doing nothing.
  const showOrgFilter = currentMenu?.key === "reviews" && screen !== "review-templates" && !currentMenu?.locked;

  // Passed down with the id so a screen can say WHICH organization it is showing, and
  // offer a way out. An empty screen that says "no scans yet" while a filter is quietly
  // hiding them is worse than no filter at all — it reports a fact that is not true.
  const reviewOrgName = effectiveOrgId
    ? (organizations.find((o) => String(o.id) === String(effectiveOrgId))?.name || "")
    : "";

  return (
    // The plan is provided to every screen, so a button inside one (the Snapshot button,
    // a lead magnet type) can lock itself without fetching the plan again.
    <PlanContext.Provider value={plan}>
    {/* id, because the account panel portals into this node rather than into <body>.
        Every --v2-* token is scoped to .v2 (see styles/tokens.css), so a panel rendered
        outside this element resolves none of them and paints with no background at all. */}
    <div id="v2-root" className={`v2${isDarkMode ? " dark" : ""}`}>
      <div className={`v2-shell${railOpen ? " rail-open" : ""}${drawerOpen ? " drawer-open" : ""}`}>
        {/* Only ever visible below the drawer breakpoint, where it covers the page behind
            the open rail. A tap anywhere on it closes the drawer, which is how every
            drawer on a phone behaves and saves aiming for a close button. */}
        <button type="button" className="v2-nav-scrim" aria-label="Close menu"
                tabIndex={drawerOpen ? 0 : -1} onClick={() => setDrawerOpen(false)} />
        <nav className="v2-rail" aria-label="Menus" ref={railRef}>
          <div className="v2-rail-brand">
            <img className="v2-mark" src="/images/pipsqueak-mark.png" alt="" />
            <span className="v2-rail-wordmark">Pipsqueak</span>
          </div>
          <button
            type="button"
            className="v2-rail-item v2-rail-toggle"
            aria-expanded={railOpen}
            aria-label={railOpen ? "Collapse menu labels" : "Expand menu labels"}
            title={railOpen ? "Collapse menu" : "Expand menu"}
            onClick={() => setRailOpen((v) => !v)}
          >
            {railOpen ? <TbLayoutSidebarLeftCollapse size={17} /> : <TbLayoutSidebarLeftExpand size={17} />}
            {railOpen && <span className="v2-rail-label">Collapse</span>}
          </button>
          {menus.map((m) => {
            const { key, icon: Icon, label, items } = m;
            const lit = !inAccount && currentMenu?.key === key;
            const flies = hasFlyout(m);
            const flying = flyKey === key;
            return (
              <button
                key={key}
                type="button"
                data-menu={key}
                // The flyout names the area once it opens; a native tooltip on top of it
                // would just repeat the label. Kept where nothing else would say it.
                title={!flies && !railOpen ? label : undefined}
                aria-label={label}
                aria-current={lit ? "true" : undefined}
                aria-expanded={flies ? flying : undefined}
                aria-controls={flies && flying ? "v2-flyout" : undefined}
                className={`v2-rail-item${lit ? " on" : ""}${flying ? " flying" : ""}`}
                onPointerEnter={(e) => {
                  pointerType.current = e.pointerType;
                  if (e.pointerType !== "mouse") return;
                  if (flies) openFly(key, e.currentTarget, flyKey ? 0 : 90);
                  else if (flyKey) closeFlySoon();
                }}
                onPointerLeave={(e) => { if (e.pointerType === "mouse") closeFlySoon(); }}
                onPointerDown={(e) => { pointerType.current = e.pointerType; }}
                // Keyboard: focusing an item opens its panel, ArrowRight moves into it.
                // :focus-visible keeps a mouse click (which also focuses) from counting.
                onFocus={(e) => { if (flies && e.currentTarget.matches(":focus-visible")) openFly(key, e.currentTarget, 0); }}
                onKeyDown={(e) => {
                  if (e.key !== "ArrowRight" || !flies) return;
                  e.preventDefault();
                  openFly(key, e.currentTarget, 0);
                  requestAnimationFrame(() => flyRef.current?.querySelector("button, input")?.focus());
                }}
                onClick={(e) => {
                  // Touch has no hover, so the first tap shows the panel; the second goes.
                  if (pointerType.current === "touch" && flies && !flying) {
                    openFly(key, e.currentTarget, 0);
                    return;
                  }
                  setMenu(key);
                  // Selecting a menu opens its first screen, so a click always lands
                  // somewhere; the flyout is for going anywhere else in the area.
                  if (items[0]) go(items[0].key);
                }}
              >
                <Icon size={17} />
                {railOpen && <span className="v2-rail-label">{label}</span>}
                {m.locked && <TbLock size={12} className="v2-rail-lock" aria-label="Not in your plan" />}
                {railOpen && flies && <TbChevronRight size={13} className="v2-rail-caret" aria-hidden="true" />}
              </button>
            );
          })}

          {/* The drawer's own index, shown only while the drawer is open (CSS hides it on
              a desktop rail). The icon rail depends on hover flyouts to reach anything
              below an area, and hover does not exist on touch — so here every screen is
              listed under its area and reachable in one tap. Rendered from the same
              `menus` array the rail is, so an area cannot appear in one and not the
              other. */}
          <div className="v2-drawer-nav">
            {menus.map((m) => (
              <div key={m.key} className="v2-drawer-group">
                <div className="v2-drawer-label">{m.label}</div>
                {m.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`v2-drawer-link${screen === item.key ? " on" : ""}`}
                    aria-current={screen === item.key ? "page" : undefined}
                    onClick={() => go(item.key)}
                  >
                    {item.icon ? <item.icon size={16} /> : <span className="v2-drawer-dot" />}
                    <span>{item.label}</span>
                    {item.locked && <TbLock size={12} className="v2-nav-lock" aria-label="Not in your plan" />}
                  </button>
                ))}
              </div>
            ))}
          </div>

          <span className="v2-rail-spacer" />
          {/* The standing way to upgrade: on Community, and during the Pro trial — a trial
              workspace is "Pro" but has not bought anything yet. */}
          {plan.loaded && (!plan.isPro || plan.onTrial) && (
            <button type="button" title="Upgrade to Pro" aria-label="Upgrade to Pro"
                    className="v2-rail-item v2-rail-upgrade" onClick={plan.upgrade}>
              <TbSparkles size={17} />
              <span className="v2-rail-label">Upgrade to Pro</span>
            </button>
          )}
          {/* Below the spacer with Sign out rather than up among the menus: fetching the
              scanner is an errand, not one of the places someone works. It still gets a
              standing slot, because burying it in the account panel meant hunting for it.
              Highlighted from `screen` directly — it belongs to no menu, so currentMenu
              cannot speak for it. */}
          <button
            type="button"
            title="Download the Scanner"
            aria-label="Download the Scanner"
            data-tour="download"
            aria-current={screen === "downloads" ? "true" : undefined}
            className={`v2-rail-item${screen === "downloads" ? " on" : ""}`}
            onClick={() => go("downloads")}
          >
            <TbDownload size={17} />
            <span className="v2-rail-label">Download the Scanner</span>
          </button>
          <button type="button" title="Sign out" aria-label="Sign out" className="v2-rail-item" onClick={logout}>
            <TbLogout size={17} />
            <span className="v2-rail-label">Sign out</span>
          </button>
          <button
            type="button"
            ref={accountRef}
            className={`v2-rail-user${accountOpen || inAccount ? " on" : ""}`}
            aria-current={inAccount ? "true" : undefined}
            aria-haspopup="true"
            aria-expanded={accountOpen}
            aria-label="Account and settings"
            title={!accountOpen && !railOpen ? "Account and settings" : undefined}
            onPointerEnter={(e) => {
              pointerType.current = e.pointerType;
              // Touch has no hover: the tap below opens it instead.
              if (e.pointerType === "mouse") openAccountSoon(accountOpen ? 0 : 90);
            }}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") closeAccountSoon(); }}
            onPointerDown={(e) => { pointerType.current = e.pointerType; }}
            // Keyboard: focusing the avatar opens the panel, the same way focusing a rail
            // item opens its flyout. :focus-visible keeps a mouse click from counting
            // twice, which would toggle it straight back shut.
            onFocus={(e) => { if (e.currentTarget.matches(":focus-visible")) { clearAccountTimer(); setAccountOpen(true); } }}
            onClick={() => { clearAccountTimer(); setAccountOpen((v) => !v); }}
          >
            <span className="v2-avatar">{(me?.name || "?").slice(0, 2).toUpperCase()}</span>
            <span className="v2-rail-label v2-rail-username">{me?.name || "Signed in"}</span>
          </button>
        </nav>

        {/* The context column is gone — the rail flyout carries each area's pages now — but
            a screen can still own one: the assessment editor lists its categories here
            through NavSlot. Always in the DOM so the portal has a host; shell.css hides it
            until a screen puts something in it. */}
        <nav className="v2-nav v2-nav--slot" aria-label="Section">
          <div className="v2-nav-slot" id="v2-nav-slot" />
        </nav>

        <div className="v2-col">
          <div className="v2-topbar">
            {/* The way into the rail once it is off-canvas. Hidden above the drawer
                breakpoint, where the rail is simply there. */}
            <button
              type="button"
              className="v2-burger"
              aria-label="Menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen((v) => !v)}
            >
              <TbMenu2 size={18} />
            </button>
            {/* Navigable, not decorative. The trail was flat text, so the one obvious
                way back up from a leaf screen did nothing when clicked. Everything up to
                the last crumb is a button; the last one is where you already are, so it
                stays text and says so with aria-current. */}
            <nav className="v2-crumbs" aria-label="Breadcrumb">
              <button type="button" className="v2-crumb" onClick={() => go(DEFAULT_SCREEN)}>
                {me?.tenant_name || "Workspace"}
              </button>
              {crumbsFor(screen).map((crumb, i, all) => (
                <React.Fragment key={crumb.key}>
                  <span className="v2-crumb-sep" aria-hidden="true">/</span>
                  {i === all.length - 1
                    ? <b aria-current="page">{crumb.label}</b>
                    : <button type="button" className="v2-crumb" onClick={() => go(crumb.key)}>{crumb.label}</button>}
                </React.Fragment>
              ))}
            </nav>
            {/* A screen that owns its own breadcrumb fills this; the default above is
                hidden by CSS when it does. */}
            <div className="v2-crumb-slot" id="v2-crumb-slot" />
            {/* The Business reviews organization filter. It lived in the context column;
                the top bar is where the rest of a screen's scope controls already sit. */}
            {showOrgFilter && (
              <div className={`v2-orgfilter${effectiveOrgId ? " on" : ""}`}>
                <label htmlFor="v2-review-org">Showing</label>
                <select
                  id="v2-review-org"
                  className="v2-input"
                  value={effectiveOrgId}
                  onChange={(e) => setReviewOrgId(e.target.value)}
                >
                  <option value="">All organizations</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                {/* Visible whenever the filter is narrowing. The select alone is easy to
                    miss after navigating away and back, which is how "there are no scans"
                    gets believed. */}
                {effectiveOrgId && (
                  <button type="button" className="v2-orgfilter-clear" onClick={() => setReviewOrgId("")}>
                    Clear
                  </button>
                )}
              </div>
            )}
            <div className="v2-topbar-slot" id="v2-topbar-slot" />
            <div className="v2-topbar-actions">
              {hasPageTour(screen) && !screenLocked && (
                <Button className="v2-howto" data-tour="how-it-works" title="How this page works" onClick={runPageTour}>
                  <TbHelpCircle size={15} />
                  <span>How it works</span>
                </Button>
              )}
              <ThemeToggle />
              <Button icon className="v2-bell" title="Notifications" aria-label="Notifications" onClick={() => go("notifications")}>
                <TbBell size={15} />
                {me?.unreadCount > 0 && <span className="v2-bell-dot">{me.unreadCount}</span>}
              </Button>
              {/* The way out. Anyone who tries this interface and hits trouble must be
                  able to leave from wherever they are, not hunt through settings. */}
              {/* <Button onClick={() => setUiVersion("v1")} title="Return to the previous interface">
                <TbArrowBackUp size={15} />
                <span>Old UI</span>
              </Button> */}
            </div>
          </div>

          <main className="v2-main" ref={mainRef}>
            <TrialBanner />
            {screenLocked ? (
              <LockedScreen gate={plan.lockCopyKey(screenLock)} />
            ) : (
              <Screen screenKey={screen} routeParam={routeParam} me={me} isAdmin={isAdmin} isPro={isPro} onNavigate={go}
                      reviewOrgId={effectiveOrgId}
                      reviewOrgName={reviewOrgName}
                      onClearReviewOrg={() => setReviewOrgId("")} />
            )}
          </main>
        </div>
      </div>

      {flyKey && (
        <NavFlyout
          ref={flyRef}
          id="v2-flyout"
          menu={menus.find((m) => m.key === flyKey)}
          anchor={flyAnchor}
          screen={screen}
          canSee={canSee}
          isLocked={planLocked}
          organizations={organizations}
          onGo={go}
          onBuilder={openBuilder}
          onOpenTemplate={openTemplate}
          onPickReviewOrg={pickReviewOrg}
          onMouseEnter={clearFlyTimer}
          onMouseLeave={closeFlySoon}
        />
      )}

      <AccountMenu
        open={accountOpen}
        onClose={() => { clearAccountTimer(); setAccountOpen(false); }}
        // Hovering the panel cancels the close the pointer started when it left the
        // avatar; leaving the panel starts it again. Without the first, the panel shuts
        // while the pointer is travelling towards it.
        onPointerEnter={clearAccountTimer}
        onPointerLeave={closeAccountSoon}
        triggerRef={accountRef}
        me={me}
        isAdmin={isAdmin}
        onNavigate={go}
        onStartTour={requestTour}
      />
      <PlanPrompts />
    </div>
    </PlanContext.Provider>
  );
};

export default ShellV2;
